import React, { lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { AccountFeatureGuard } from 'guard/AccountFeatureGuard';
import { asyncRouteLoader } from 'utils/asyncRouteLoader';
import { AIProviderPage } from 'containers/AIProvider';
import RouteAnalytics from './RouteAnalytics';

const User = asyncRouteLoader(
  () => import('containers/User'),
  [
    {
      type: 'reducer',
      reducerKey: 'roles',
      importPromise: () => import('containers/RolesConfig/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/RolesConfig/sagas'),
    },
  ]
);
const UserManagement = asyncRouteLoader(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  () => import('containers/UserManagement'),
  [
    {
      type: 'reducer',
      reducerKey: 'userManagement',
      importPromise: () => import('containers/UserManagement/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'user',
      importPromise: () => import('containers/UserDetails/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/UserManagement/sagas'),
    },
  ]
);
const RolesConfig = lazy(() => import('containers/RolesConfig'));
const UserSSO = lazy(() => import('pages/app/users/sso'));
const UserDetails = asyncRouteLoader(
  () => import('containers/UserDetails'),
  [
    {
      type: 'reducer',
      reducerKey: 'user',
      importPromise: () => import('containers/UserDetails/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/UserDetails/sagas'),
    },
  ]
);
const Provisions = lazy(() => import('containers/Provisions'));
const Domains = lazy(() => import('containers/Domains'));
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const UserGroups = lazy(() => import('pages/app/users/groups'));
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const UserIntegrations = lazy(
  () => import('pages/app/users/integrations/Integrations')
);

const WhiteLabelling = asyncRouteLoader(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  () => import('containers/WhiteLabelling'),
  [
    {
      type: 'reducer',
      reducerKey: 'whiteLabelling',
      importPromise: () => import('containers/WhiteLabelling/_utils/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/WhiteLabelling/_utils/sagas'),
    },
  ]
);
const EnvironmentVariables = asyncRouteLoader(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  () => import('containers/EnvironmentVariables'),
  [
    {
      type: 'reducer',
      reducerKey: 'environmentVariables',
      importPromise: () => import('containers/EnvironmentVariables/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/EnvironmentVariables/sagas'),
    },
  ]
);
const AuditLog = asyncRouteLoader(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  () => import('containers/AuditLog'),
  [
    {
      type: 'reducer',
      reducerKey: 'auditLog',
      importPromise: () => import('containers/AuditLog/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/AuditLog/sagas'),
    },
  ]
);
const TicketFields = lazy(() => import('containers/Ticketing/TicketFields'));
const TicketLayouts = lazy(() => import('containers/Ticketing/TicketLayouts'));
const CycleTimes = lazy(() => import('containers/Ticketing/CycleTimes'));
const RequesterPortalAdminPage = lazy(
  () => import('containers/Ticketing/RequesterPortal/AdminPage')
);
const AuditPortalAdminPage = lazy(
  () => import('containers/Ticketing/AuditPortal/AdminPage')
);
const OnboardingWizardAdminPage = lazy(
  () => import('containers/Ticketing/OnboardingWizard/AdminPage')
);
const EmailIntakeLogsPage = lazy(
  () => import('containers/Ticketing/EmailIntakeLogs')
);
export const accountSettingsRoutes = () => (
  <Route
    path="users"
    element={
      <RouteAnalytics pageName="Users">
        <User />
      </RouteAnalytics>
    }
  >
    <Route index element={<Navigate to="management" replace />} />
    <Route
      path="management"
      element={
        <RouteAnalytics pageName="User-Manage">
          <UserManagement />
        </RouteAnalytics>
      }
    />
    <Route
      path="roles"
      element={
        <RouteAnalytics pageName="Roles">
          <RolesConfig />
        </RouteAnalytics>
      }
    />
    <Route
      path="sso"
      element={
        <RouteAnalytics pageName="sso">
          <UserSSO />
        </RouteAnalytics>
      }
    />

    <Route
      path="provisioning"
      element={
        <RouteAnalytics pageName="Integrations">
          <Provisions />
        </RouteAnalytics>
      }
    />
    <Route
      path="domains"
      element={
        <RouteAnalytics pageName="Integrations">
          <Domains />
        </RouteAnalytics>
      }
    />
    <Route
      path="groups/*"
      element={
        <RouteAnalytics pageName="groups">
          <UserGroups />
        </RouteAnalytics>
      }
    />
    <Route
      path="integrations/*"
      element={
        <RouteAnalytics pageName="Integrations">
          <UserIntegrations />
        </RouteAnalytics>
      }
    />
    <Route
      path="whitelabelling"
      element={
        <RouteAnalytics pageName="Whitelabelling">
          <WhiteLabelling />
        </RouteAnalytics>
      }
    />
    <Route
      path="ticket-fields"
      element={
        <AccountFeatureGuard feature="ticketing">
          <RouteAnalytics pageName="TicketFields">
            <TicketFields />
          </RouteAnalytics>
        </AccountFeatureGuard>
      }
    />
    <Route
      path="ticket-layouts"
      element={
        <AccountFeatureGuard feature="ticketing">
          <RouteAnalytics pageName="TicketLayouts">
            <TicketLayouts />
          </RouteAnalytics>
        </AccountFeatureGuard>
      }
    />
    <Route
      path="cycle-times"
      element={
        <RouteAnalytics pageName="Cycle Times">
          <CycleTimes />
        </RouteAnalytics>
      }
    />
    <Route
      path="requester-portal"
      element={
        <RouteAnalytics pageName="Requester Portal">
          <RequesterPortalAdminPage />
        </RouteAnalytics>
      }
    />
    <Route
      path="audit-portal"
      element={
        <RouteAnalytics pageName="Audit Portal">
          <AuditPortalAdminPage />
        </RouteAnalytics>
      }
    />
    <Route
      path="onboarding-wizard"
      element={
        <RouteAnalytics pageName="AI Onboarding">
          <OnboardingWizardAdminPage />
        </RouteAnalytics>
      }
    />
    <Route
      path="email-intake-log"
      element={
        <RouteAnalytics pageName="Email Intake Logs">
          <EmailIntakeLogsPage />
        </RouteAnalytics>
      }
    />
    <Route
      path="environmentvariables"
      element={
        <RouteAnalytics pageName="EnvironmentVariables">
          <EnvironmentVariables />
        </RouteAnalytics>
      }
    />
    <Route
      path="audit"
      element={
        <RouteAnalytics pageName="AuditLog">
          <AuditLog />
        </RouteAnalytics>
      }
    />
    <Route
      path=":user_id"
      element={
        <RouteAnalytics pageName="User-Details">
          <UserDetails />
        </RouteAnalytics>
      }
    />
    <Route
      path="ai-provider"
      element={
        <RouteAnalytics pageName="AI Provider">
          <AIProviderPage />
        </RouteAnalytics>
      }
    />
  </Route>
);
